import { useState } from "react";

import type { IConcurrencyGroup } from "@/@types/gamification";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useGetConcurrencyGroupsQuery } from "@/services/gamification";

import {
  CONCURRENCY_GROUP_SEARCH_DEBOUNCE_MS,
  ConcurrencyGroupListState,
} from "../constants/concurrency-group";
import {
  findConcurrencyGroup,
  normalizeGroupName,
  resolveConcurrencyGroupListState,
} from "../utils/concurrency-group";

interface UseConcurrencyGroupComboboxArgs {
  onSelect: (name: string) => void;
  excludeCampaignId?: number;
}

export interface UseConcurrencyGroupComboboxResult {
  open: boolean;
  setOpen: (open: boolean) => void;
  search: string;
  setSearch: (value: string) => void;
  groups: IConcurrencyGroup[];
  listState: ConcurrencyGroupListState;
  retry: () => void;
  createName: string | null;
  hasSearch: boolean;
  select: (name: string) => void;
}

export function useConcurrencyGroupCombobox({
  onSelect,
  excludeCampaignId,
}: UseConcurrencyGroupComboboxArgs): UseConcurrencyGroupComboboxResult {
  const [open, setOpenState] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const typed: string = normalizeGroupName(search);
  const debouncedSearch: string = useDebouncedValue(
    typed,
    CONCURRENCY_GROUP_SEARCH_DEBOUNCE_MS,
  );

  const { data, isFetching, isError, refetch } = useGetConcurrencyGroupsQuery({
    search: debouncedSearch || undefined,
    excludeCampaignId,
  });

  const groups: IConcurrencyGroup[] = data ?? [];
  const createName: string | null =
    typed && !findConcurrencyGroup(groups, typed) ? typed : null;

  const setOpen = (next: boolean): void => {
    setOpenState(next);
    if (!next) setSearch("");
  };

  const select = (name: string): void => {
    onSelect(normalizeGroupName(name));
    setOpen(false);
  };

  return {
    open,
    setOpen,
    search,
    setSearch,
    groups,
    listState: resolveConcurrencyGroupListState({
      isLoading: isFetching,
      isError,
      hasData: data !== undefined,
    }),
    retry: refetch,
    createName,
    hasSearch: typed.length > 0,
    select,
  };
}
